// Utilitários de validação

import {
  ValidationResult,
  ResponsavelOrcamento,
  DadosValores,
  DadosAssinatura,
  DadosAceite,
} from "../types/orcamento-fornecedor.types";

export const validarCPF = (cpf: string): boolean => {
  const cleaned = cpf.replace(/\D/g, "");

  if (cleaned.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cleaned)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let resto = 11 - (soma % 11);
  let digitoVerificador1 = resto === 10 || resto === 11 ? 0 : resto;

  if (digitoVerificador1 !== parseInt(cleaned.charAt(9))) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  resto = 11 - (soma % 11);
  let digitoVerificador2 = resto === 10 || resto === 11 ? 0 : resto;

  return digitoVerificador2 === parseInt(cleaned.charAt(10));
};

export const validarEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

export const validarCampoObrigatorio = (valor: string): boolean => {
  return valor.trim().length > 0;
};

// Validação da Etapa 1
export const validarEtapa1 = (
  cnaeIds: string[],
  responsavel: Partial<ResponsavelOrcamento>,
  telefoneEmpresa?: string,
): ValidationResult => {
  const errors: Record<string, string> = {};

  // A chave precisa ser a mesma que a tela lê. Era `cnae` aqui e `cnaeId` na
  // tela: quem avançava sem escolher era barrado sem ver o motivo.
  if (cnaeIds.length === 0) {
    errors.cnaeIds = "Selecione ao menos um CNAE";
  }

  // Dez dígitos é o mínimo de um fixo com DDD; celular tem onze.
  const digitosTelefone = (telefoneEmpresa ?? "").replace(/\D/g, "");
  if (digitosTelefone.length < 10) {
    errors.telefoneEmpresa = digitosTelefone
      ? "Informe o telefone com DDD"
      : "Telefone é obrigatório";
  }

  if (!responsavel.nome || !validarCampoObrigatorio(responsavel.nome)) {
    errors.nome = "Nome é obrigatório";
  }

  if (!responsavel.email || !validarEmail(responsavel.email)) {
    errors.email = "Email inválido";
  }

  if (!responsavel.cpf || !validarCPF(responsavel.cpf)) {
    errors.cpf = "CPF inválido";
  }

  if (!responsavel.cargo || !validarCampoObrigatorio(responsavel.cargo)) {
    errors.cargo = "Cargo é obrigatório";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

// Validação da Etapa 2
export const validarEtapa2 = (
  dados: Partial<DadosValores>,
): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!dados.dataOrcamento) {
    errors.dataOrcamento = "Data do orçamento é obrigatória";
  }

  if (!dados.validadeDias || dados.validadeDias <= 0) {
    errors.validadeDias = "Validade deve ser maior que zero";
  } else if (dados.validadeDias < 30) {
    errors.validadeDias = "Validade mínima é de 30 dias";
  }

  if (!dados.itens || Object.keys(dados.itens).length === 0) {
    errors.itens = "Preencha o valor de todos os itens";
  } else {
    // Verificar se todos os valores são válidos
    const valoresInvalidos = Object.values(dados.itens).some(
      (v: any) => !v || v <= 0,
    );
    if (valoresInvalidos) {
      errors.itens = "Todos os itens devem ter valor unitário maior que zero";
    }
  }

  if (!dados.formaPagamento?.trim()) {
    errors.formaPagamento = "Forma de pagamento é obrigatória";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

// Validação da Etapa 3
export const validarEtapa3 = (
  dados: Partial<DadosAssinatura>,
): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!dados.tipo) {
    errors.tipo = "Selecione o tipo de assinatura";
  }

  if (!dados.assinaturaData || dados.assinaturaData.trim().length === 0) {
    errors.assinatura = "Assinatura é obrigatória";
  } else {
    // Aceitar tanto data URLs quanto URLs externas (Azure Blob Storage)
    const isDataUrl = dados.assinaturaData.startsWith("data:image/");
    const isHttpUrl =
      dados.assinaturaData.startsWith("http://") ||
      dados.assinaturaData.startsWith("https://");

    if (!isDataUrl && !isHttpUrl) {
      errors.assinatura = "Assinatura em formato inválido";
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

// Validação da Etapa 4
export const validarEtapa4 = (
  dados: Partial<DadosAceite>,
): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!dados.termo2) {
    errors.termo2 = "Você deve aceitar o termo";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};
